import styled, {css} from 'styled-components'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'

export type NotificationContextBarVariant = 'wide' | 'hosted'

/**
 * The bar carries no positioning and no z-index of its own — the wide form gets both from
 * NotificationContextBarHost's reserved row, and the hosted form sits inside its host.
 */
export const StyledNotificationContextBar = styled.div<{
    $variant: NotificationContextBarVariant
    $isConfirmed: boolean
}>(
    ({theme: {palette, spacing, mediaQueries}, $variant, $isConfirmed}) => css`
        display: grid;
        align-items: center;
        gap: ${spacing * 2}px;
        background-color: ${$isConfirmed ? palette.success['50'] : palette.primary['50']};
        border-bottom: 1px solid ${$isConfirmed ? palette.success['200'] : palette.primary['200']};

        ${$variant === 'wide'
            ? css`
                  grid-template-columns: auto minmax(0, 1fr) auto auto;
                  grid-template-areas: 'back description actions trailing';
                  padding: ${spacing * 2}px ${spacing * 4}px;

                  /* Narrow viewports cannot hold the three clusters on one row (FR-030) */
                  ${mediaQueries.l} {
                      grid-template-columns: minmax(0, 1fr) auto;
                      grid-template-areas:
                          'back trailing'
                          'description actions';
                  }
              `
            : css`
                  /* Sits inside its host (the 500px conversations panel), in place of that
                     host's own header: it takes the host's width, matches its horizontal
                     padding, and carries no positioning or z-index of its own. The clusters
                     cannot share a single row at that width, so they stack. */
                  width: 100%;
                  grid-template-columns: minmax(0, 1fr) auto;
                  grid-template-areas:
                      'back trailing'
                      'description actions';
                  padding: ${spacing * 2}px ${spacing * 4}px ${spacing * 3}px;
              `}
    `
)

export const StyledNotificationContextBack = styled.div`
    grid-area: back;
    min-width: 0;
`

export const StyledNotificationContextDescription = styled(Flexbox)<{
    $variant: NotificationContextBarVariant
    $isConfirmed: boolean
}>(
    ({theme: {palette, mediaQueries}, $variant, $isConfirmed}) => css`
        grid-area: description;
        min-width: 0;
        /* The description survives the transition unchanged; only the icon carries the new
           state, so it takes the success colour while the text stays fully legible. */
        color: ${$isConfirmed ? palette.success['700'] : palette.neutral['900']};
        ${$variant === 'wide' &&
        css`
            justify-content: center;

            ${mediaQueries.l} {
                justify-content: flex-start;
            }
        `}

        & > svg {
            flex-shrink: 0;
        }

        /* A long description must never push the actions off the bar (FR-030) */
        & > span {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        & b {
            font-weight: 600;
        }
    `
)

export const StyledNotificationContextActions = styled(Flexbox)`
    grid-area: actions;
    flex-shrink: 0;
    justify-self: end;
`

export const StyledNotificationContextTrailing = styled.div`
    grid-area: trailing;
    justify-self: end;
`

/**
 * Announces the active → confirmed transition to assistive technology without moving focus.
 */
export const StyledVisuallyHidden = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`
